<template>
  <tile class="opc-addon-config">
    <foldable v-model="isExpanded" :arrow="hasSelectedPlan" :edit="hasSelectedPlan" :title="title">
      <template #head>
        <div class="opc-zm-base-card__head">
          <div class="opc-zm-base-card__head-left">
            <img :src="logo" class="opc-zm-base-card__logo" :alt="title + ' Icon'" tabindex="-1" />
          </div>
          <div class="opc-zm-base-card__head-right">
            <h2 class="opc-zm-base-card__name text-capitalize">
              <span :class="[isAdd2CartOrEditFlow ? 'opc-addon-config__title--add' : '']">{{
                title }}</span>
              <i v-if="hasSelectedPlan && !isAdd2CartOrEditFlow" check-icon class="zm-icon-success main-icon" aria-hidden="true" />
            </h2>
            <div v-if="hasSelectedPlan && !isExpanded && isAdd2CartOrEditFlow"
              class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
              <storage-content :selected-plan="selectedPlan" />
            </div>
            <div v-if="!isAdd2CartOrEditFlow && !isExpanded"
              class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
              <storage-content v-if="hasSelectedPlan" :selected-plan="selectedPlan" />
            </div>
          </div>
        </div>
      </template>
      <div class="divide-line"></div>
      <div class="opc-addon-config__main ldk-cmr">
        <p class="opc-addon-config__main__title">{{ $t('billing.cmr_ldk.intro') }}
          <zm-tooltip class="opc-addon-config-row--tips" floating-content-class="sdk-cmr-tips" :aria-label="`Learn more about ${title}`" placement="bottom" @show="showTooltip">
            <template #content>
              <div v-html="tips" />
            </template>
            <tips-icon type="info" :aria-label="`Learn more about ${title}`" />
          </zm-tooltip>
          </p>
        <zm-radio-group v-model="selectedPlanCode" :enter-select="true" :before-change="beforeChangeHandler" ref="radioGroup"
          aria-label="Select a capacity" class="opc-addon-config__main__plan opc-card-radio">
          <zm-radio data-testid="checkout-radio-67" v-for="plan in planList" :key="plan.plan_code" :label="plan.plan_code" :class="planList.length === 1? 'zoom-radio-single':''">
            <p class="opc-addon-config__main__plan__capacity">
              <span>{{ plan.cartCapacity }} {{ plan.cartCapacityUnit }}</span>
              <CycleCurrentBadge :value="plan" />
            </p>
            <div class="opc-addon-config__main__plan__price">
              <div class="opc-addon-config__main__plan__price--amount">
                {{ $money(plan.amount, { forceDiff: true }) }}
              </div>
              <div class="opc-addon-config__main__plan__price--unit">
                {{ $t(`billing.common.per_billing_cycle.${plan.billing_cycle}`) }}<priceVATTip />
              </div>
            </div>
          </zm-radio>
        </zm-radio-group>
        <div class="opc-zm-base-card__main__footer">
          <div class="opc-zm-base-card__btn--wrap">
            <zm-button data-testid="checkout-button-68" v-if="!isAdd2CartOrEditFlow && canRmv" type="link" ghost @click="removePlanByKey"
              :aria-label="'remove ' + title + ' plan'">{{ $t('billing.common.remove') }}</zm-button>
          </div>
        </div>
        <preview-payment class="opc-addon-config__preview-payment" />
      </div>
    </foldable>
  </tile>
</template>

<script>
import { get } from "lodash";
import CMRCommon from './mixin';
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import StorageContent from './components/storage-content.vue';
import { planTypes } from '@/common/constants';
import plansMixin from '@/plans/mx/plan';
import { translation } from '@/plugins/i18n';
import { pageConfig } from '@/common/service/page-config';
import PreviewPayment from '@/views/checkout/payment/preview-payment';
import CycleCurrentBadge from '@/components/service/config-billing-cycle/cycle-current-badge.vue';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';

export default {
  name: 'LDKCMR-Config',
  mixins: [CMRCommon, plansMixin],
  components: {
    PreviewPayment,
    Foldable,
    Tile,
    StorageContent,
    CycleCurrentBadge,
  },
  data() {
    return {
      isExpanded: true,
      billingCycle: '',
      selectedPlanCode: '',
    };
  },
  computed: {
    ...mapState({
      planMapFlat: state => state.ShoppingCart.planMapFlat,
    }),
    ...mapGetters(['getSelectedPlans', 'basicConfig']),
    planList() {
      return this.planMapFlat.filter((p) => p.type === planTypes.LDKCMR);
    },
    selectedPlan() {
      return this.planList.find((item) => item.plan_code === this.selectedPlanCode);
    },
    cartSelectedPlan() {
      return get(this.getSelectedPlans.filter((p) => p.type === planTypes.LDKCMR), [0]);
    },
    hasSelectedPlan() {
      return !!this.selectedPlanCode;
    },
    capacityText(){
      const { capacity } = this.planList[0]
      return this.getCapacityText(capacity)
    },
    editPlanListTipsTxt(){
      return this.planList.map(k=> [this.getCapacityText(k.capacity),k.excessPrice,k.minuteExcessPrice])
    },
    tips(){
      const {minuteCapacity, excessPrice, minuteExcessPrice } = this.planList[0]
      return `${translation('billing.cmr_ldk.tips_base')} <br />`+
              (this.$context.is.edit?
              `${translation('billing.cmr.important_tips')}<br />
                 ${translation('billing.cmr_ldk.tips_tb_2',this.editPlanListTipsTxt[0])} <br />
                 ${translation('billing.cmr_ldk.tips_tb_2',this.editPlanListTipsTxt[1])}`
                :
              `${translation('billing.cmr_ldk.tips', [
                  this.capacityText,
                  minuteCapacity,
                ])}<br />
                - ${translation('billing.cmr_ldk.tips_2',[excessPrice])} <br />
                - ${translation('billing.cmr_ldk.tips_3',[minuteExcessPrice])}`)
    },
    canRmv(){
      return this.isShowRemoveBtn
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'removePlan']),
    ...mapActions(['switchSelectPlan']),
    beforeChangeHandler(done, newPlanCode) {
      this.onPlanSelect(newPlanCode);
      done(true);
    },
    onPlanSelect(newPlanCode) {
      const oldPlanCode = this.selectedPlanCode;

      if (this.selectedPlanCode !== newPlanCode) {
        this.switchSelectPlan([oldPlanCode, newPlanCode, undefined]);
        this.selectedPlanCode = newPlanCode;
      } else {
        this.updateSelectPlan({
          plan_code: newPlanCode,
          cartInfo: { selected_number: 1 },
        });
      }

      const plan = this.planList?.find((plan) => plan.plan_code === newPlanCode);
      plan && this.$tracking.addOnBuyInteraction(getTrackingProductNameByPlanType(planTypes.LDKCMR), `${plan?.cartCapacity}${plan?.cartCapacityUnit}`);
    },
    onPlanRemove() {
      this.removePlan(this.selectedPlanCode);
      this.selectedPlanCode = '';
    },
    // For A11Y
    getFirstFocusableControl() {
      let firstRadioEl = this.$refs.radioGroup.$el.querySelector('.zbo-radio').querySelector('input[type="radio"]');
      return Promise.resolve(firstRadioEl);
    },
    getCapacityText(number) {
      const suffix = {
        gb: ' GB',
        tb: ' TB',
      };
      if (number < 1024) {
        return number + suffix.gb;
      } else {
        return number / 1024 + suffix.tb;
      }
    },
    showTooltip() {
      this.$tracking.buyInteraction('video sdk pay as you go add-ons',`${this.trackingPlanKey} - ${this.capacityText} -  tooltip`)
    },
    collapseCard() {
      this.isExpanded = false;
    },
  },
  watch: {
    cartSelectedPlan: {
      immediate: true,
      handler(plan) {
        this.selectedPlanCode = plan?.plan_code ?? "";
      },
    },
    isExpanded: {
      handler(val) {
        this.$tracking.addOnBuyInteraction('sdk recording accordion', val ? 'expand' : 'collapse');
      },
    },
  },
  mounted(){
    const oldPlanCode = pageConfig?.oldPlanCode;
    if(oldPlanCode === 'ldkcmr_3TB_monthly'){
      this.onPlanSelect('ldkcmr_5TB_monthly')
    }
    if(!oldPlanCode && this.$context.is.buyNow){
      this.onPlanSelect('ldkcmr_1TB_monthly')
    }
    this.$context.$on('collapse-plan-card', this.collapseCard);
  },
};
</script>

<style lang="scss">
@import '@/styles/zoom-card.scss';
@import '@/styles/plans/addon-config.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.ldk-cmr {
  .zbo-radio {
    &.zbo-radio-single{
      flex: 0 0 calc(100% - 8px);
    }

    flex: 0 0 calc(100% - 8px);
  }
}

.opc-addon-config {
  &__main {
    &__plan {
      display: flex;
      flex-wrap: wrap;
      margin-top: 16px;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);

      .zbo-radio {

        &__wrap {
          width: 100%;
        }

        &__label {
          &--inner {
            width: 100%;
          }

          &.is-disabled {

            .opc-addon-config__main__plan__price--amount,
            .opc-addon-config__main__plan__price--unit {
              color: var(--zoom-color-state-disable, #ADB1B8);
            }
          }
        }
      }

      &__capacity,
      &__price {
        line-height: 150%;
      }

      &__capacity {
        font-weight: 400;
        font-size: 16px;
        display: flex;
        align-items: center;

        &-recommend {
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(96.59deg, #084845 10.36%, #09A639 89.64%);
          border-radius: 100px;
          padding: 0px 8px;
          color: var(--zoom-color-fill-default, #FFFFFF);
          font-weight: 400;
          font-size: 12px;
          line-height: 16px;
          margin-left: 8px;
        }
      }

      &__price {
        font-weight: 590;
        font-size: 24px;

        &--amount {
          font-size: 24px;
          font-style: normal;
          font-weight: 590;
          line-height: 28px;
          color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
        }

        &--unit {
          font-size: 14px;
          font-weight: 400;
          line-height: 18px;
          color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
        }
      }
    }

    &__footer {
      margin-top: 16px;
    }

  }

  &__btn {
    &--wrap {
      margin-top: 24px;

      .zbo-button {
        padding: 0px;
      }
    }
  }

  &__preview-payment {
    margin-top: 16px;
  }
}

.sdk-cmr-tips{
 min-width: 650px;
 @include media(mobile, tablet){
  min-width: auto;
 }
 .zbo-tooltip {
  max-width: 650px;
 }
}
</style>
